import events from "add-event-listener";
import PropTypes from "prop-types";
import * as React from "react";

import { smoothScrollBy } from "../../../Engine/utils";
import { MultilineRowHeightContextRoot } from "../../Internals/ControlContexts/MultilineRowHeightContext";
import { emptyRef, isChildOfType, nullElement, pullContext } from "../../TypingUtils";
import { UnitLayoutingContext } from "../../FormParts/Unit/Internals/UnitBorderContext/UnitLayoutingContext";
import { areArraysNotEqual } from "../../../../../Common/ArrayUtils";

import { Footer } from "./Footer";
import { HorizontalScrollContainer } from "./HorizontalScrollContainer/HorizontalScrollContainer";
import { Rows } from "./Rows";
import * as styles from "./StickyTable.styles";
import { StickyTableData } from "./Body/StickyTableData";
import { StickyTableHeader } from "./StickyTableHeader";
import { StickyTableSidebar } from "./StickyTableSidebar";
import { MultilinePagerContainer } from "./MultilineRow/MultilinePager/MultilinePagerContainer";

interface StickyTableProps {
    children: React.ReactNode;
    columnWidth: number[];
    heightRows?: number[];
    isHeaderFixed: boolean;
    left: number;
    sideSize: number;
    width: number;
    optional: boolean;
}

interface StickyTableContext extends UnitLayoutingContext {
    getFooterHeight: () => number;
    getScrollLeft?: () => number;
    onScrollContainerWidthChange?: (handler: (width: number) => void) => void;
    paddingLeft: number;
    paddingRight: number;
}

export class StickyTable extends React.Component<StickyTableProps> {
    public table: null | HTMLDivElement = emptyRef;
    public data: null | HTMLDivElement = emptyRef;
    public header = React.createRef<StickyTableHeader>();
    public scrollWidth?: number;

    public static defaultProps = {
        width: 0,
        left: 0,
        sideSize: 0,
    };

    public static contextTypes = {
        getFooterHeight: PropTypes.func,
        getScrollLeft: PropTypes.func,
        onScrollContainerWidthChange: PropTypes.func,
        paddingLeft: PropTypes.number,
        paddingRight: PropTypes.number,
    };

    public static childContextTypes = {
        getHeaderHeight: PropTypes.func,
        processTableDataPaste: PropTypes.func,
        fillMultiline: PropTypes.func,
    };

    public getChildContext = () => ({
        getHeaderHeight: this.getHeaderHeight,
    });

    public componentDidMount(): void {
        // firefox < 61 version not support focusin
        if (this.data != undefined) {
            events.addEventListener(this.data, "focus", this._handleOnFocus, true);
        }

        this.getContext().onScrollContainerWidthChange?.((width: number) => {
            this.scrollWidth = width;
            this.forceUpdate();
        });
    }

    public shouldComponentUpdate(nextProps: StickyTableProps): boolean {
        return (
            nextProps.width !== this.props.width ||
            nextProps.left !== this.props.left ||
            nextProps.heightRows !== this.props.heightRows ||
            areArraysNotEqual(nextProps.columnWidth, this.props.columnWidth)
        );
    }

    public componentWillUnmount(): void {
        if (this.data != undefined) {
            events.removeEventListener(this.data, "focus", this._handleOnFocus);
        }
    }

    public render(): JSX.Element {
        const { header, sidebar, data, footer, pager } = this.getStickyElements();
        const sidebarWidth = this.props.columnWidth
            .slice(0, this.props.sideSize)
            .reduce((a, b) => a + b, this.getContext().paddingLeft);

        const fullContainerStyle = {
            marginLeft: -this.getContext().paddingLeft,
            marginRight: -this.getContext().paddingRight,
            width: this.props.width,
        };

        const innerWidth = this.data ? this.props.width - (sidebar ? sidebarWidth : 0) : 0;

        const stopScrollPoints: number[] = [];
        if (this.props.columnWidth.length - this.props.sideSize > 2) {
            let columnOffset = 0;
            stopScrollPoints.push(columnOffset);
            const bodyColumnWidths = this.props.columnWidth.slice(this.props.sideSize);
            for (const columnWidth of bodyColumnWidths) {
                columnOffset += columnWidth;
                stopScrollPoints.push(columnOffset);
            }
        }

        return (
            <div style={fullContainerStyle}>
                <div css={styles.root} ref={this._refTable}>
                    <MultilineRowHeightContextRoot>
                        <HorizontalScrollContainer
                            innerWidth={innerWidth}
                            scrollInner={this.data}
                            sidebarWidth={sidebarWidth}
                            stopPoints={stopScrollPoints}
                            table={this.table}
                            width={this.props.width}>
                            <Rows
                                data={data}
                                header={header}
                                innerWidth={innerWidth}
                                paddingLeft={this.getContext().paddingLeft}
                                setData={this._refData}
                                sidebar={sidebar}
                                pager={pager}
                                footer={footer}
                            />
                        </HorizontalScrollContainer>
                    </MultilineRowHeightContextRoot>
                </div>
            </div>
        );
    }

    private getContext(): StickyTableContext {
        return pullContext<StickyTableContext>(this);
    }

    private readonly _refTable = (el: null | HTMLDivElement) => {
        this.table = el;
    };

    private readonly _refData = (el: null | HTMLDivElement) => {
        this.data = el;
    };

    private readonly getHeaderHeight = (): number => this.header.current?.getHeight() ?? 0;

    private readonly _handleOnFocus = (_event: FocusEvent) => {
        if (document.activeElement == undefined || !document.activeElement.nodeName) {
            return;
        }
        const nodeName = document.activeElement.nodeName.toLowerCase();

        if (nodeName !== "input" && nodeName !== "button") {
            return;
        }

        const elementRect = document.activeElement.getBoundingClientRect();

        if (elementRect.top < this.getHeaderHeight() + 20) {
            smoothScrollBy(elementRect.top - this.getHeaderHeight() - 20);
        }

        const footerHeight = this.getContext().getFooterHeight();
        const screenHeight = document.documentElement.offsetHeight;

        if (screenHeight - elementRect.bottom < footerHeight + 20) {
            smoothScrollBy(footerHeight + 20 - screenHeight + elementRect.bottom);
        }
    };

    private getStickyElements(): {
        header: JSX.Element | null;
        sidebar: JSX.Element | null;
        data: JSX.Element | null;
        pager: JSX.Element | null;
        footer: JSX.Element | null;
    } {
        let sidebar: JSX.Element | null = nullElement;
        let header: JSX.Element | null = nullElement;
        let data: JSX.Element | null = nullElement;
        let pager: JSX.Element | null = nullElement;
        let footer: JSX.Element | null = nullElement;

        const context = this.getContext();

        const getScrollLeft = context.getScrollLeft;
        const scrollLeft = getScrollLeft ? getScrollLeft() : 0;
        const pageWidth = this.scrollWidth ?? this.props.width;
        const stickyWidth = Math.min(this.props.width, pageWidth + scrollLeft);

        React.Children.forEach(this.props.children ?? [], child => {
            if (isChildOfType(child, StickyTableSidebar)) {
                sidebar = React.cloneElement(child, {
                    columnWidth: this.props.columnWidth.slice(0, this.props.sideSize),
                    width: this.props.width,
                    heightRows: this.props.heightRows,
                });
            }
            if (isChildOfType(child, StickyTableHeader)) {
                header = React.cloneElement(child, {
                    isFixed: this.props.isHeaderFixed,
                    columnWidth: this.props.columnWidth,
                    table: this.table,
                    width: stickyWidth,
                    left: scrollLeft ? this.props.left : 0,
                    // @ts-ignore Я не смог заставить вызваться перегрузку для cloneElenent, которая возъмёт атрибут ref
                    ref: this.header,
                });
            }

            if (isChildOfType(child, StickyTableData)) {
                data = React.cloneElement(child, {
                    columnWidth: this.props.columnWidth.slice(this.props.sideSize),
                    heightRows: this.props.heightRows,
                });
            }
            if (isChildOfType(child, MultilinePagerContainer)) {
                pager = React.cloneElement(child);
            }
            if (isChildOfType(child, Footer)) {
                footer = React.cloneElement(child, {
                    width: stickyWidth,
                    columnWidth: this.props.columnWidth,
                });
            }
        });

        return { header: header, sidebar: sidebar, data: data, pager: pager, footer: footer };
    }
}
